import type { ShiftBreak } from '@libs/data-access/queries'
import { List } from 'immutable'
import type { MomentInput } from 'moment'

import type { BreakRow } from '../components/BreakRowsContainer/BreakRowsContainer'

import convertBreakToDate from './convertBreakToDate'

export type GetBreaksToSendParams = {
  breaks?: { value: BreakRow }[]
  momentStart: MomentInput
  momentEnd?: MomentInput
  shiftBreakList: ShiftBreak[]
}

/**
 * Retrieve all the breaks from the form and compute if we need to add those or delete them from the DB
 */
export const getBreaksToSend = ({
  breaks,
  momentStart,
  shiftBreakList
}: GetBreaksToSendParams) => {
  const breaksToAdd = breaks?.reduce<ShiftBreak[]>(
    (acc, { value: { plannedDuration, timeRange, id } }) => {
      const isEmptyTimeRange = !timeRange?.length || timeRange.includes('')

      // Prevent creating breaks with empty value
      if (!plannedDuration) {
        return acc
      }

      let breakAttributes: {
        planned_starts_at: null | string
        planned_ends_at: null | string
        real_starts_at: null | string
        real_ends_at: null | string
      } = {
        planned_starts_at: null,
        planned_ends_at: null,
        real_starts_at: null,
        real_ends_at: null
      }

      if (!isEmptyTimeRange) {
        // [HH:mm, HH:mm]
        const [breakStart, breakEnd] = timeRange || []
        const breakDates = convertBreakToDate(momentStart, breakStart, breakEnd)

        breakAttributes = {
          planned_starts_at: breakDates.start.format(),
          planned_ends_at: breakDates.end.format(),
          real_starts_at: breakDates.start.format(),
          real_ends_at: breakDates.end.format()
        }
      }

      // @ts-ignore to fix later because it's a mess...
      acc.push({
        // @ts-ignore
        id: typeof id === 'string' ? undefined : id,
        planned_duration: plannedDuration,
        real_duration: plannedDuration,
        ...breakAttributes
      })

      return acc
    },
    []
  )

  // The API need an object with {id : breakID, _destroy: 1} to delete a break.
  // Since we delete the break in the form, we need to compare the two array and
  // locate where the ID is missing to understand if we need to delete the break

  const breaksToDelete = shiftBreakList?.reduce((acc, breakItem) => {
    const isNewBreak = breaksToAdd?.some(
      newBreak => newBreak?.id === breakItem.id
    )

    if (!isNewBreak && breakItem.id !== undefined) {
      return acc.push({ id: breakItem.id, _destroy: 1 })
    }

    return acc
  }, List())

  return breaksToAdd
    ? [...breaksToAdd, ...breaksToDelete.toArray()]
    : breaksToDelete.toArray()
}
